import React from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useI18n } from "@/contexts/I18nContext";
import { ArrowLeft } from "lucide-react";

const COMPANY = {
  name: "TelcaVoIP International EU",
  address: "Ul. Zbozowa 4A, 70-653 Szczecin, Poland",
  vat: "PL8513242072",
  supportEmail: "support@telcavoip.eu",
};

export default function Terms() {
  const { t, locale } = useI18n();
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <Header />
      <article className="mx-auto max-w-3xl px-6 py-16 lg:px-10">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B0E5C]"
          data-testid="terms-back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("legal.back_home")}
        </Link>
        <p className="text-xs uppercase tracking-[0.2em] text-[#9ACD32] font-bold">
          {t("legal.terms_last_updated")}
        </p>
        <h1 className="mt-3 font-display text-5xl font-extrabold tracking-tight text-[#05073B]">
          {t("legal.terms_title")}
        </h1>
        <p className="mt-6 text-lg text-slate-600">{t("legal.terms_intro")}</p>

        <CompanyCard locale={locale} />

        <div className="prose prose-slate mt-10 max-w-none text-[15px] leading-relaxed text-slate-700">
          {locale === "it" && <ItalianTerms />}
          {locale === "pl" && <PolishTerms />}
          {locale !== "it" && locale !== "pl" && <EnglishTerms />}
        </div>
      </article>
      <Footer />
    </div>
  );
}

function CompanyCard({ locale }) {
  const labels = {
    en: { title: "Operator", reg: "Registered office", vat: "VAT", contact: "Contact" },
    it: { title: "Titolare", reg: "Sede legale", vat: "Partita IVA", contact: "Contatto" },
    pl: { title: "Operator", reg: "Siedziba", vat: "NIP/VAT UE", contact: "Kontakt" },
  };
  const l = labels[locale] || labels.en;
  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-[#FAFAF8] p-6" data-testid="terms-company-card">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9ACD32]">{l.title}</p>
      <h3 className="mt-2 font-display text-2xl font-bold text-[#05073B]">{COMPANY.name}</h3>
      <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs uppercase tracking-wider text-slate-500">{l.reg}</dt>
          <dd className="mt-1 text-slate-700">{COMPANY.address}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-slate-500">{l.vat}</dt>
          <dd className="mt-1 font-mono text-slate-700">{COMPANY.vat}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-slate-500">{l.contact}</dt>
          <dd className="mt-1">
            <a
              href={`mailto:${COMPANY.supportEmail}`}
              className="text-[#0B0E5C] underline-offset-4 hover:underline"
              data-testid="terms-support-email"
            >
              {COMPANY.supportEmail}
            </a>
          </dd>
        </div>
      </dl>
    </div>
  );
}

function EnglishTerms() {
  return (
    <>
      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-10 mb-3">1. Acceptance of Terms</h2>
      <p>By accessing or using NEURACOM by TelcaVoIP ({`"Service"`}), provided by {COMPANY.name} (VAT {COMPANY.vat}), you agree to these Terms of Service. If you do not agree, do not use the Service.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">2. Service Description</h2>
      <p>NEURACOM is an AI workspace offering multi-model chat (Claude, GPT, Gemini), image generation, document Q&A and an AI code builder, delivered on a subscription basis. The Service is powered by third-party AI providers (Anthropic, OpenAI, Google) and infrastructure partners.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">3. Account</h2>
      <p>You must provide accurate information at registration and you must be at least 18 years old. You are responsible for safeguarding your credentials and for all activity carried out from your account.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">4. Subscription, Billing & Refunds</h2>
      <p>Paid plans are billed monthly in EUR (Starter €29, Pro €79, Business €199). Usage quotas reset at the start of each billing period. You can cancel anytime from your billing page; access continues until the end of the paid period and is automatically downgraded to the Free tier afterwards. As a digital service consumed immediately, the 14-day EU right of withdrawal does not apply once you start using the Service after a paid plan begins (Art. 38 EU Directive 2011/83).</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">5. Acceptable Use</h2>
      <p>You may not use the Service to: (a) generate content that is illegal, harmful, harassing, defamatory or infringing intellectual-property rights; (b) attempt to bypass quotas or rate limits; (c) reverse-engineer the Service; (d) resell or relabel the output as your own without disclosing AI involvement where required by law.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">6. Content & Intellectual Property</h2>
      <p>You retain ownership of the prompts you submit and the outputs generated for you, subject to the licenses that AI providers require to process your data. You grant {COMPANY.name} a limited, non-exclusive licence to process content strictly to deliver the Service.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">7. Disclaimer</h2>
      <p>The Service is provided {`"as is"`} and {`"as available"`}. AI-generated content may be inaccurate, biased or incomplete. Do not rely on outputs for legal, medical, financial or other professional decisions without independent verification.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">8. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law, the aggregate liability of {COMPANY.name} for all claims arising out of or related to the Service is limited to the amounts you paid in the 12 months immediately preceding the claim. We are not liable for indirect, incidental or consequential damages.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">9. Suspension & Termination</h2>
      <p>We may suspend or terminate your account if you breach these Terms or if continued provision would expose us to legal risk. You may delete your account anytime from settings.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">10. Governing Law & Jurisdiction</h2>
      <p>These Terms are governed by the laws of the Republic of Poland. Disputes shall be resolved exclusively by the competent courts of Szczecin, Poland, unless mandatory consumer-protection rules require otherwise.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">11. Changes</h2>
      <p>We may update these Terms; the latest version will always be available at this page. Material changes will be notified by email at least 14 days in advance.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">12. Contact</h2>
      <p>Questions about these Terms: <a href={`mailto:${COMPANY.supportEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.supportEmail}</a>.</p>
    </>
  );
}

function ItalianTerms() {
  return (
    <>
      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-10 mb-3">1. Accettazione dei Termini</h2>
      <p>Utilizzando NEURACOM by TelcaVoIP ({`"Servizio"`}), fornito da {COMPANY.name} (Partita IVA {COMPANY.vat}), accetti i presenti Termini di Servizio. Se non accetti, non utilizzare il Servizio.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">2. Descrizione del Servizio</h2>
      <p>NEURACOM è uno spazio di lavoro AI che offre chat multi-modello (Claude, GPT, Gemini), generazione immagini, Q&A su documenti e un AI code builder, erogato in abbonamento. Il Servizio si appoggia a provider AI di terze parti (Anthropic, OpenAI, Google) e partner infrastrutturali.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">3. Account</h2>
      <p>Devi fornire informazioni accurate al momento della registrazione e devi avere almeno 18 anni. Sei responsabile della custodia delle credenziali e di tutte le attività svolte dal tuo account.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">4. Abbonamento, Fatturazione e Recesso</h2>
      <p>I piani a pagamento sono fatturati mensilmente in EUR (Starter €29, Pro €79, Business €199). Le quote di utilizzo si resettano all{`'`}inizio di ogni periodo di fatturazione. Puoi cancellare in qualsiasi momento dalla pagina di billing; l{`'`}accesso prosegue fino al termine del periodo pagato e successivamente passa automaticamente al piano Free. Trattandosi di un servizio digitale fruito immediatamente, il diritto di recesso UE di 14 giorni non si applica una volta iniziato l{`'`}uso del Servizio dopo l{`'`}avvio di un piano a pagamento (Art. 38 Direttiva UE 2011/83).</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">5. Uso Accettabile</h2>
      <p>Non puoi usare il Servizio per: (a) generare contenuti illegali, dannosi, molesti, diffamatori o lesivi di diritti di proprietà intellettuale; (b) aggirare quote o rate limit; (c) decompilare il Servizio; (d) rivendere o etichettare gli output come tuoi senza dichiarare il coinvolgimento di AI quando richiesto dalla legge.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">6. Contenuti e Proprietà Intellettuale</h2>
      <p>Mantieni la proprietà dei prompt che invii e degli output generati, fatte salve le licenze necessarie ai provider AI per elaborare i dati. Concedi a {COMPANY.name} una licenza limitata e non esclusiva per elaborare i contenuti al solo scopo di erogare il Servizio.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">7. Disclaimer</h2>
      <p>Il Servizio è fornito {`"così com'è"`} e {`"secondo disponibilità"`}. I contenuti generati dall{`'`}AI possono essere imprecisi, parziali o incompleti. Non basare decisioni legali, mediche, finanziarie o professionali sugli output senza verifica indipendente.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">8. Limitazione di Responsabilità</h2>
      <p>Nei limiti consentiti dalla legge, la responsabilità complessiva di {COMPANY.name} per qualsiasi reclamo connesso al Servizio è limitata agli importi pagati nei 12 mesi precedenti il reclamo. Non rispondiamo di danni indiretti, incidentali o consequenziali.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">9. Sospensione e Risoluzione</h2>
      <p>Possiamo sospendere o terminare il tuo account in caso di violazione dei Termini o quando la prosecuzione del servizio comporti rischi legali. Puoi cancellare l{`'`}account in qualsiasi momento dalle impostazioni.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">10. Legge Applicabile e Foro</h2>
      <p>I presenti Termini sono regolati dalla legge della Repubblica di Polonia. Le controversie saranno risolte in via esclusiva dai tribunali competenti di Szczecin (Polonia), salvo norme imperative a tutela del consumatore.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">11. Modifiche</h2>
      <p>Possiamo aggiornare questi Termini; la versione corrente è sempre disponibile in questa pagina. Le modifiche sostanziali verranno notificate via email con almeno 14 giorni di preavviso.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">12. Contatti</h2>
      <p>Domande sui Termini: <a href={`mailto:${COMPANY.supportEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.supportEmail}</a>.</p>
    </>
  );
}

function PolishTerms() {
  return (
    <>
      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-10 mb-3">1. Akceptacja Regulaminu</h2>
      <p>Korzystając z NEURACOM by TelcaVoIP ({`"Usługa"`}), świadczonej przez {COMPANY.name} (NIP {COMPANY.vat}), akceptujesz niniejszy Regulamin. Jeśli nie akceptujesz, nie korzystaj z Usługi.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">2. Opis Usługi</h2>
      <p>NEURACOM to środowisko AI oferujące czat z wieloma modelami (Claude, GPT, Gemini), generowanie obrazów, Q&A dokumentów oraz AI code builder, świadczone w abonamencie. Usługa wykorzystuje zewnętrznych dostawców AI (Anthropic, OpenAI, Google) i partnerów infrastrukturalnych.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">3. Konto</h2>
      <p>Przy rejestracji musisz podać dokładne informacje i mieć ukończone 18 lat. Odpowiadasz za bezpieczeństwo swoich danych logowania i wszelką aktywność wykonaną z Twojego konta.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">4. Subskrypcja, Płatności i Odstąpienie</h2>
      <p>Plany płatne są rozliczane miesięcznie w EUR (Starter €29, Pro €79, Business €199). Limity zużycia resetują się na początku każdego okresu rozliczeniowego. Możesz anulować w dowolnym momencie ze strony rozliczeń; dostęp trwa do końca opłaconego okresu, a następnie konto jest automatycznie zdegradowane do planu Free. Jako usługa cyfrowa konsumowana natychmiast, 14-dniowe prawo odstąpienia UE nie ma zastosowania od momentu rozpoczęcia korzystania z Usługi po aktywacji planu płatnego (art. 38 dyrektywy UE 2011/83).</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">5. Dopuszczalne użycie</h2>
      <p>Nie wolno wykorzystywać Usługi do: (a) generowania treści nielegalnych, szkodliwych, nękających, zniesławiających lub naruszających prawa własności intelektualnej; (b) prób omijania limitów; (c) inżynierii wstecznej Usługi; (d) odsprzedaży lub etykietowania wyników jako własnych bez ujawnienia udziału AI, jeśli wymaga tego prawo.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">6. Treść i własność intelektualna</h2>
      <p>Zachowujesz własność przesyłanych promptów i generowanych wyników, z zastrzeżeniem licencji wymaganych przez dostawców AI. Udzielasz {COMPANY.name} ograniczonej, niewyłącznej licencji na przetwarzanie treści wyłącznie w celu świadczenia Usługi.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">7. Wyłączenie odpowiedzialności</h2>
      <p>Usługa jest świadczona {`"tak jak jest"`} i {`"w miarę dostępności"`}. Treści generowane przez AI mogą być niedokładne, stronnicze lub niekompletne. Nie polegaj na wynikach przy decyzjach prawnych, medycznych, finansowych ani innych zawodowych bez niezależnej weryfikacji.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">8. Ograniczenie odpowiedzialności</h2>
      <p>W maksymalnym dopuszczalnym zakresie łączna odpowiedzialność {COMPANY.name} z tytułu roszczeń związanych z Usługą jest ograniczona do kwot zapłaconych w 12 miesiącach poprzedzających roszczenie. Nie odpowiadamy za szkody pośrednie, uboczne ani następcze.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">9. Zawieszenie i rozwiązanie</h2>
      <p>Możemy zawiesić lub zamknąć konto w przypadku naruszenia Regulaminu lub gdy dalsze świadczenie usługi narażałoby nas na ryzyko prawne. Możesz usunąć konto w dowolnym momencie z ustawień.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">10. Prawo właściwe i jurysdykcja</h2>
      <p>Regulamin podlega prawu Rzeczypospolitej Polskiej. Spory rozstrzygają wyłącznie właściwe sądy w Szczecinie (Polska), z zastrzeżeniem bezwzględnie obowiązujących przepisów konsumenckich.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">11. Zmiany</h2>
      <p>Możemy aktualizować niniejszy Regulamin; aktualna wersja zawsze będzie dostępna na tej stronie. O istotnych zmianach poinformujemy e-mailem z co najmniej 14-dniowym wyprzedzeniem.</p>

      <h2 className="font-display text-2xl font-bold text-[#05073B] mt-8 mb-3">12. Kontakt</h2>
      <p>Pytania dotyczące Regulaminu: <a href={`mailto:${COMPANY.supportEmail}`} className="text-[#0B0E5C] underline-offset-4 hover:underline">{COMPANY.supportEmail}</a>.</p>
    </>
  );
}
